import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PROJECT_MANAGEMENT_URL } from '@insite-store360/shared';
import { Observable, catchError, map } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';

@Injectable({
  providedIn: 'root',
})
export class LeadService {
  private readonly URL_PATH = `${PROJECT_MANAGEMENT_URL}/lead`;
  private readonly LEAD_PRODUCT_URL_PATH = `${PROJECT_MANAGEMENT_URL}/leadProduct`;

  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService
  ) { }

  public lost(leadId: number, remarks: string): Observable<{ message: string; }> {
    return this.lostLead(leadId, remarks, null, 0);
  }

  public lostLead(leadId: number, remarks: string, reasonId: number | null, userId: number = 0): Observable<{ message: string; }> {
    let url = `${this.URL_PATH}/leadlost`;
    return this.http
      .post<{ message: string; }>(url, { leadId, remarks, reasonId })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get all manual models that need mapping
   */
  public getManualModels(countryIds?: number[]): Observable<any[]> {
    let params = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    return this.http
      .get<any[]>(`${this.LEAD_PRODUCT_URL_PATH}/manual-models`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get SAP models for dropdown (with search and country filter)
   */
  public getSapModelsForDropdown(searchFilter: string, countryId: number | null): Observable<any[]> {
    let params = new HttpParams().set('searchFilter', searchFilter);
    if (countryId !== null && countryId !== undefined) {
      params = params.set('countryId', countryId.toString());
    }
    return this.http
      .get<any>(`${this.LEAD_PRODUCT_URL_PATH}/sap-models`, { params })
      .pipe(
        map((res: any) => {
          const data = res?.result || [];
          return data.map((m: any) => ({
            ...m,
            id: m.pnc + '_' + m.model,
            label: `${m.model} (${m.pnc})`,
            value: m.pnc + '_' + m.model
          }));
        }),
        catchError(this.errorHandler.handleError)
      );
  }

  /**
   * Update manual model mapping across all leads
   */
  public updateManualMapping(payload: { manualModel: string, targetModel: string, targetPnc: string, category: string, subCategory: string, countryId: number }): Observable<{ message: string }> {
    return this.http
      .post<{ message: string }>(`${this.LEAD_PRODUCT_URL_PATH}/update-mapping`, payload)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public requestforChange(leadId: number, remarks: string): Observable<{ message: string; }> {
    // Call the new API endpoint: POST /requestChnages/save/:leadId
    const url = `${PROJECT_MANAGEMENT_URL}/requestChnages/save/${leadId}`;
    return this.http
      .post<{ message: string; }>(url, { remarks })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getRequestChangeHistory(leadId: number): Observable<any[]> {
    const url = `${PROJECT_MANAGEMENT_URL}/requestChnages/getAll/${leadId}`;
    return this.http
      .get<any[]>(url)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get next reference number for a country
   */
  public getNextReferenceNumber(countryId: number): Observable<{ referenceNo: string }> {
    const params = new HttpParams().set('countryId', countryId.toString());
    return this.http
      .get<{ referenceNo: string }>(`${this.URL_PATH}/getNextReferenceNumber`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Create a new lead
   */
  public createLead(payload: any): Observable<any> {
    return this.http
      .post<any>(`${this.URL_PATH}/create`, payload)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get all products for dropdown (with search and country filter)
   */
  public getProductsForDropdown(searchFilter?: string, countryShortName?: string): Observable<{ result: { productId: number; model: string; pnc: string; displayText: string }[] }> {
    let queryParams = new HttpParams();
    if (searchFilter && searchFilter.trim() !== '') {
      queryParams = queryParams.set('searchFilter', searchFilter.trim());
    }
    if (countryShortName && countryShortName.trim() !== '') {
      queryParams = queryParams.set('countryShortName', countryShortName.trim());
    }
    return this.http
      .get<{ result: { productId: number; model: string; pnc: string; displayText: string }[] }>(`${this.URL_PATH}/getProductsForDropdown`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get product details by model
   * Gets retail_selling_price from cost_preparation table if countryId is provided
   * Also fetches product details from temp_product_details table
   */
  public getProductDetails(model: string, countryId?: number, pnc?: string): Observable<{ productId: number; model: string; category: string; subCategory: string; retailSellingPrice: number | null; productDetails: Array<{ detail: string; moreInformation: string }>; externalImageUrl?: string | null; productTitle?: string }> {
    let params = new HttpParams().set('model', model);
    if (countryId !== undefined && countryId !== null) {
      params = params.set('countryId', countryId.toString());
    }
    if (pnc !== undefined && pnc !== null && pnc.trim()) {
      params = params.set('pnc', pnc.trim());
    }
    return this.http
      .get<{ productId: number; model: string; category: string; subCategory: string; retailSellingPrice: number | null; productDetails: Array<{ detail: string; moreInformation: string }>; externalImageUrl?: string | null; productTitle?: string }>(`${this.LEAD_PRODUCT_URL_PATH}/getProductDetails`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Create a lead product with images
   */
  public createLeadProduct(formData: FormData): Observable<any> {
    return this.http
      .post<any>(`${this.LEAD_PRODUCT_URL_PATH}/createLeadProduct`, formData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get all products for a lead
   */
  public getLeadProducts(leadId: number): Observable<any[]> {
    const params = new HttpParams().set('leadId', leadId.toString());
    return this.http
      .get<any[]>(`${this.LEAD_PRODUCT_URL_PATH}/getLeadProducts`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Update a lead product with images
   */
  public updateLeadProduct(leadProductId: number, formData: FormData): Observable<any> {
    return this.http
      .put<any>(`${this.LEAD_PRODUCT_URL_PATH}/updateLeadProduct/${leadProductId}`, formData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Delete a lead product
   */
  public deleteLeadProduct(leadProductId: number): Observable<any> {
    return this.http
      .delete<any>(`${this.LEAD_PRODUCT_URL_PATH}/deleteLeadProduct/${leadProductId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get default terms and conditions by country
   */
  public getDefaultTermsAndConditions(countryId?: number): Observable<{ termsHtml: string }> {
    let params = new HttpParams();
    if (countryId !== undefined && countryId !== null) {
      params = params.set('countryId', countryId.toString());
    }
    return this.http
      .get<{ termsHtml: string }>(`${this.URL_PATH}/getDefaultTermsAndConditions`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get users for dropdown (with search, country filter, and role filter)
   */
  public getUsersForDropdown(searchFilter?: string, countryIds?: number[], roleIds?: number[]): Observable<{ result: { userId: number; fullName: string; email: string }[] }> {
    let queryParams = new HttpParams();
    if (searchFilter && searchFilter.trim() !== '') {
      queryParams = queryParams.set('searchFilter', searchFilter.trim());
    }
    if (countryIds && countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', countryIds.join(','));
    }
    if (roleIds && roleIds.length > 0) {
      queryParams = queryParams.set('roleIds', roleIds.join(','));
    }
    return this.http
      .get<{ result: { userId: number; fullName: string; email: string }[] }>(`${this.URL_PATH}/getUsersForDropdown`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Update a lead
   */
  public updateLead(leadId: number, payload: any, signatureFile?: File): Observable<any> {
    const formData = new FormData();

    // Append all payload fields
    Object.keys(payload).forEach(key => {
      if (payload[key] !== undefined && payload[key] !== null) {
        formData.append(key, payload[key]);
      }
    });

    // Append signature file if provided
    if (signatureFile) {
      formData.append('signature', signatureFile);
    }

    return this.http
      .put<any>(`${this.URL_PATH}/updateLead/${leadId}`, formData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Request approval for a lead
   */
  public requestApproval(leadId: number, approverIds: number[], termsAndConditions: string, signatureFile?: File): Observable<{ message: string; lead: any }> {
    const formData = new FormData();
    formData.append('approverIds', JSON.stringify(approverIds));
    formData.append('termsAndConditions', termsAndConditions);

    // Append signature file if provided
    if (signatureFile) {
      formData.append('signature', signatureFile);
    }

    return this.http
      .post<{ message: string; lead: any }>(`${this.URL_PATH}/requestApproval/${leadId}`, formData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get all leads with pagination and search
   */
  public getAllLeads(
    pageIndex?: number,
    pageSize?: number,
    searchFilter?: string,
    isMyPendingApproval?: boolean,
    statusIds?: number[],
    countryIds?: number[],
    customerIds?: number[],
    projectTypeIds?: number[],
    probabilityIds?: number[],
    createdById?: number,
    salesPersonIds?: number[],
    targetCurrency?: string,
    yearIds?: number[],
    monthIds?: number[],
    deliveryYearIds?: number[],
    sortBy?: string,
    sortDirection?: string
  ): Observable<{ result: any[]; paginatorValue: any }> {
    let queryParams = new HttpParams();
    if (pageIndex !== undefined && pageIndex !== null) {
      queryParams = queryParams.set('pageIndex', pageIndex.toString());
    }
    if (pageSize !== undefined && pageSize !== null) {
      queryParams = queryParams.set('pageSize', pageSize.toString());
    }
    if (searchFilter && searchFilter.trim() !== '') {
      queryParams = queryParams.set('searchFilter', searchFilter.trim());
    }
    if (isMyPendingApproval !== undefined && isMyPendingApproval !== null) {
      queryParams = queryParams.set('isMyPenending', isMyPendingApproval.toString());
    }
    if (statusIds && statusIds.length > 0) {
      statusIds.forEach(id => {
        queryParams = queryParams.append('statusIds', id.toString());
      });
    }
    if (countryIds && countryIds.length > 0) {
      countryIds.forEach(id => {
        queryParams = queryParams.append('countryIds', id.toString());
      });
    }
    if (customerIds && customerIds.length > 0) {
      customerIds.forEach(id => {
        queryParams = queryParams.append('customerIds', id.toString());
      });
    }
    if (projectTypeIds && projectTypeIds.length > 0) {
      projectTypeIds.forEach(id => {
        queryParams = queryParams.append('projectTypeIds', id.toString());
      });
    }
    if (probabilityIds && probabilityIds.length > 0) {
      probabilityIds.forEach(id => {
        queryParams = queryParams.append('probabilityIds', id.toString());
      });
    }
    if (createdById !== undefined && createdById !== null) {
      queryParams = queryParams.set('createdById', createdById.toString());
    }
    if (salesPersonIds && salesPersonIds.length > 0) {
      salesPersonIds.forEach(id => {
        queryParams = queryParams.append('salesPersonIds', id.toString());
      });
    }
    if (targetCurrency) {
      queryParams = queryParams.set('targetCurrency', targetCurrency);
    }
    if (yearIds && yearIds.length > 0) {
      yearIds.forEach(id => {
        queryParams = queryParams.append('yearIds', id.toString());
      });
    }
    if (monthIds && monthIds.length > 0) {
      monthIds.forEach(id => {
        queryParams = queryParams.append('monthIds', id.toString());
      });
    }
    if (deliveryYearIds && deliveryYearIds.length > 0) {
      deliveryYearIds.forEach(id => {
        queryParams = queryParams.append('deliveryYearIds', id.toString());
      });
    }
    if (sortBy) {
      queryParams = queryParams.set('sortBy', sortBy);
    }
    if (sortDirection) {
      queryParams = queryParams.set('sortDirection', sortDirection);
    }
    return this.http
      .get<{ result: any[]; paginatorValue: any }>(`${this.URL_PATH}/getAll`, { params: queryParams });
  }

  /**
   * Get lead by ID with all details
   */
  public getLeadById(leadId: number): Observable<any> {
    return this.http
      .get<any>(`${this.URL_PATH}/getById/${leadId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Approve a lead
   */
  public approveLead(leadId: number, remarks: string): Observable<{ message: string }> {
    return this.http
      .post<{ message: string }>(`${this.URL_PATH}/approve`, { leadId, remarks })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Bypass approval for a lead (direct to Bidding status)
   */
  public bypassApproval(leadId: number): Observable<{ message: string; lead: any }> {
    return this.http
      .post<{ message: string; lead: any }>(`${this.URL_PATH}/bypassApproval/${leadId}`, {})
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Reject a lead
   */
  public rejectLead(leadId: number, remarks: string): Observable<{ message: string }> {
    return this.http
      .post<{ message: string }>(`${this.URL_PATH}/reject`, { leadId, remarks })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getVersionHistory(leadId: number): Observable<any[]> {
    return this.http
      .get<any[]>(`${this.URL_PATH}/versionHistory/${leadId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get lead status KPI data with full filtering support
   */
  public getLeadStatusKpi(
    countryIds?: number[],
    customerIds?: number[],
    projectTypeIds?: number[],
    yearIds?: number[],
    monthIds?: number[],
    searchFilter?: string,
    salesPersonIds?: number[],
    createdById?: number
  ): Observable<{ statusId: number; statusName: string; count: number }[]> {
    let queryParams = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      countryIds.forEach(id => {
        queryParams = queryParams.append('countryIds', id.toString());
      });
    }
    if (customerIds && customerIds.length > 0) {
      customerIds.forEach(id => {
        queryParams = queryParams.append('customerIds', id.toString());
      });
    }
    if (projectTypeIds && projectTypeIds.length > 0) {
      projectTypeIds.forEach(id => {
        queryParams = queryParams.append('projectTypeIds', id.toString());
      });
    }
    if (yearIds && yearIds.length > 0) {
      yearIds.forEach(id => {
        queryParams = queryParams.append('yearIds', id.toString());
      });
    }
    if (monthIds && monthIds.length > 0) {
      monthIds.forEach(id => {
        queryParams = queryParams.append('monthIds', id.toString());
      });
    }
    if (searchFilter && searchFilter.trim() !== '') {
      queryParams = queryParams.set('searchFilter', searchFilter.trim());
    }
    if (salesPersonIds && salesPersonIds.length > 0) {
      salesPersonIds.forEach(id => {
        queryParams = queryParams.append('salesPersonIds', id.toString());
      });
    }
    if (createdById !== undefined && createdById !== null) {
      queryParams = queryParams.set('createdById', createdById.toString());
    }
    return this.http
      .get<{ statusId: number; statusName: string; count: number }[]>(`${this.URL_PATH}/getLeadStatusKpi`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get products by lead ID and batch ID
   */
  public getProductsByLeadAndBatchId(leadId: number, batchId: number): Observable<any[]> {
    const params = new HttpParams()
      .set('leadId', leadId.toString())
      .set('batchId', batchId.toString());
    return this.http
      .get<any[]>(`${this.LEAD_PRODUCT_URL_PATH}/getByleadAndBatchId`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Cancel a lead
   */
  public cancelLead(leadId: number): Observable<{ message: string }> {
    return this.http
      .post<{ message: string }>(`${this.URL_PATH}/cancel/${leadId}`, {})
      .pipe(catchError(this.errorHandler.handleError));
  }


  /**
   * Get manual model details
   */
  public getManualModel(model: string, countryId: number, pnc: string = ''): Observable<any> {
    let params = new HttpParams()
      .set('model', model)
      .set('countryId', countryId.toString());
    if (pnc) {
      params = params.set('pnc', pnc);
    }
    return this.http
      .get<any>(`${PROJECT_MANAGEMENT_URL}/manual-models/preset`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Save manual model
   */
  public saveManualModel(payload: any): Observable<any> {
    return this.http
      .post<any>(`${PROJECT_MANAGEMENT_URL}/manual-models/save-preset`, payload)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get product preset details
   */
  public getProductPreset(model: string, countryId: number, pnc: string = ''): Observable<any> {
    let params = new HttpParams()
      .set('model', model)
      .set('countryId', countryId.toString())
      .set('_t', new Date().getTime().toString());
    if (pnc) {
      params = params.set('pnc', pnc);
    }
    return this.http
      .get<any>(`${PROJECT_MANAGEMENT_URL}/product-presets/preset`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Save product preset
   */
  public saveProductPreset(payload: any): Observable<any> {
    return this.http
      .post<any>(`${PROJECT_MANAGEMENT_URL}/product-presets/save-preset`, payload)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get all manual models with filtering
   */
  public getAllManualModels(countryIds?: number[], search?: string): Observable<any[]> {
    let params = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    if (search) {
      params = params.set('search', search);
    }
    return this.http
      .get<any>(`${PROJECT_MANAGEMENT_URL}/manual-models/all-presets`, { params })
      .pipe(
        map(res => res?.result?.data || []),
        catchError(this.errorHandler.handleError)
      );
  }

  /**
   * Delete a manual model
   */
  public deleteManualModel(id: number): Observable<any> {
    return this.http
      .delete<any>(`${PROJECT_MANAGEMENT_URL}/manual-models/delete-preset/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Export manual models to Excel
   */
  public exportManualModels(countryIds?: number[], search?: string): Observable<Blob> {
    let params = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    if (search) {
      params = params.set('search', search);
    }
    return this.http
      .get(`${PROJECT_MANAGEMENT_URL}/manual-models/downloadExcelReport`, {
        params,
        responseType: 'blob'
      })
      .pipe(catchError(this.errorHandler.handleError));
  }
  /**
   * Export manual model mapping report to Excel
   */
  public exportManualMappingReport(countryIds?: number[], search?: string): Observable<Blob> {
    let params = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      params = params.set('countryIds', countryIds.join(','));
    }
    if (search) {
      params = params.set('search', search);
    }
    return this.http
      .get(`${this.LEAD_PRODUCT_URL_PATH}/downloadExcelReport`, {
        params,
        responseType: 'blob'
      })

      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Download Excel template for manual model mapping
   */
  public downloadExcelTemplate(countryId: number | null): Observable<Blob> {
    let params = new HttpParams();
    if (countryId !== null && countryId !== undefined) {
      params = params.set('countryId', countryId.toString());
    }
    return this.http
      .get(`${this.LEAD_PRODUCT_URL_PATH}/downloadExcelTemplate`, {
        params,
        responseType: 'blob'
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Import manual model mapping data from Excel
   */
  public importData(data: any[], countryId: number | null): Observable<any> {
    let params = new HttpParams();
    if (countryId !== null && countryId !== undefined) {
      params = params.set('countryId', countryId.toString());
    }
    return this.http
      .post<any>(`${this.LEAD_PRODUCT_URL_PATH}/importData`, data, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public bulkImportLeads(data: any[]): Observable<any> {
    const url = `${this.URL_PATH}/bulk-import`;
    return this.http.post(url, data);
  }

}
